import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Form,
  Select,
  Input,
  Button,
  Space,
  Divider,
  message,
} from 'antd';
import { FilterOutlined, ClearOutlined, CloseOutlined, CalendarOutlined } from '@ant-design/icons';
import { IncomeSource, SourceWallet } from '../../services/incomeService';
import dayjs, { Dayjs } from 'dayjs';
import styles from '../common/DrawerModalMobile.module.css';
import { DatePickerModal } from '../common/DatePickerModal';

const { Option } = Select;

interface FilterModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: any) => void;
  currentFilters: any;
  sources: IncomeSource[];
  wallets: SourceWallet[];
}

export const FilterModalMobile: React.FC<FilterModalMobileProps> = ({
  isOpen,
  onClose,
  onApply,
  currentFilters,
  sources,
  wallets,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        source_id: currentFilters.source_id,
        wallet_id: currentFilters.wallet_id,
        transaction_type: currentFilters.transaction_type,
        start_date: currentFilters.start_date ? dayjs(currentFilters.start_date) : undefined,
        end_date: currentFilters.end_date ? dayjs(currentFilters.end_date) : undefined,
        search: currentFilters.search,
      });
      checkActiveFilters(currentFilters);
    }
  }, [isOpen, currentFilters, form]);

  const checkActiveFilters = (filters: any) => {
    const hasFilters = Boolean(
      filters.source_id || 
      filters.wallet_id || 
      filters.transaction_type || 
      filters.start_date || 
      filters.end_date || 
      filters.search
    );
    setHasActiveFilters(hasFilters);
  };

  const handleApply = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Chuyển đổi date
      if (values.start_date) {
        values.start_date = values.start_date.format('YYYY-MM-DD');
      }
      if (values.end_date) {
        values.end_date = values.end_date.format('YYYY-MM-DD');
      }
      
      // Validation: end_date phải lớn hơn hoặc bằng start_date
      if (values.start_date && values.end_date && values.start_date > values.end_date) {
        message.error('Ngày kết thúc phải lớn hơn hoặc bằng ngày bắt đầu');
        return;
      }
      
      onApply(values);
      onClose();
      message.success('Đã áp dụng bộ lọc');
    } catch (error) {
      console.error('Filter validation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue({
      source_id: undefined,
      wallet_id: undefined,
      transaction_type: undefined,
      start_date: undefined,
      end_date: undefined,
      search: undefined,
    });
    // Áp dụng filter rỗng ngay lập tức
    onApply({
      source_id: undefined,
      wallet_id: undefined,
      transaction_type: undefined,
      start_date: undefined,
      end_date: undefined,
      search: undefined,
    });
    onClose();
    message.success('Đã xóa bộ lọc');
  };

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    checkActiveFilters(values);
  };

  const handleStartDateSelect = (date: Dayjs) => {
    form.setFieldValue('start_date', date);
    setStartDatePickerOpen(false);
  };

  const handleEndDateSelect = (date: Dayjs) => {
    form.setFieldValue('end_date', date);
    setEndDatePickerOpen(false);
  };

  const getStartDateValue = () => {
    return form.getFieldValue('start_date');
  };

  const getEndDateValue = () => {
    return form.getFieldValue('end_date');
  };

  return (
    <>
      <Drawer
        title="Bộ lọc thu nhập"
        open={isOpen}
        onClose={onClose}
        placement="bottom"
        height="70%"
        className={styles.drawer}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ 
          borderBottom: '1px solid #f0f0f0',
          padding: '16px 20px'
        }}
      >
        <div className={styles.drawerContent}>
          <div className={styles.drawerBody}>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleFormChange}
            >
              <div className={styles.formItem}>
                <Form.Item
                  label="Nguồn thu nhập"
                  name="source_id"
                >
                  <Select
                    placeholder="Chọn nguồn thu nhập"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    className={styles.select}
                  >
                    {sources.map((source) => (
                      <Option key={source.id} value={source.id}>
                        <Space>
                          <span>{source.icon}</span>
                          <span>{source.name}</span>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className={styles.formItem}>
                <Form.Item
                  label="Ví"
                  name="wallet_id"
                >
                  <Select
                    placeholder="Chọn ví"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    className={styles.select}
                  >
                    {wallets.map((wallet) => (
                      <Option key={wallet.id} value={wallet.id}>
                        <Space>
                          <span>{wallet.source_icon}</span>
                          <span>{wallet.name}</span>
                          <span style={{ color: '#666', fontSize: '12px' }}>
                            ({wallet.source_name})
                          </span>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className={styles.formItem}>
                <Form.Item
                  label="Loại giao dịch"
                  name="transaction_type"
                >
                  <Select
                    placeholder="Chọn loại giao dịch"
                    allowClear
                    className={styles.select}
                  >
                    <Option value="income">
                      <Space>
                        <span>📈</span>
                        <span>Thu nhập</span>
                      </Space>
                    </Option>
                    <Option value="expense">
                      <Space>
                        <span>📉</span>
                        <span>Chi tiêu</span>
                      </Space>
                    </Option>
                  </Select>
                </Form.Item>
              </div>

              <div className={styles.formItem}>
                <Form.Item
                  label="Từ ngày"
                  name="start_date"
                >
                  <div className={styles.dateInputContainer}>
                    <Input
                      placeholder="Chọn ngày bắt đầu"
                      readOnly
                      value={getStartDateValue()?.format('DD/MM/YYYY') || ''}
                      className={styles.dateInput}
                      suffix={<CalendarOutlined />}
                      onClick={() => setStartDatePickerOpen(true)}
                    />
                  </div>
                </Form.Item>
              </div>

              <div className={styles.formItem}>
                <Form.Item
                  label="Đến ngày"
                  name="end_date"
                >
                  <div className={styles.dateInputContainer}>
                    <Input
                      placeholder="Chọn ngày kết thúc"
                      readOnly
                      value={getEndDateValue()?.format('DD/MM/YYYY') || ''}
                      className={styles.dateInput}
                      suffix={<CalendarOutlined />}
                      onClick={() => setEndDatePickerOpen(true)}
                    />
                  </div>
                </Form.Item>
              </div>

              <div className={styles.formItem}>
                <Form.Item
                  label="Tìm kiếm"
                  name="search"
                >
                  <Input
                    placeholder="Nhập từ khóa tìm kiếm"
                    allowClear
                    className={styles.input}
                  />
                </Form.Item>
              </div>
            </Form>
          </div>

          <div className={styles.drawerFooter}>
            <div className={styles.drawerFooterRow}>
              <Button
                className={`${styles.drawerFooterButton} ${styles.default}`}
                onClick={onClose}
                icon={<CloseOutlined />}
              >
                Đóng
              </Button>
              {hasActiveFilters && (
                <Button
                  className={`${styles.drawerFooterButton} ${styles.default}`}
                  onClick={handleReset}
                  icon={<ClearOutlined />}
                >
                  Xóa bộ lọc
                </Button>
              )}
              <Button
                type="primary"
                className={`${styles.drawerFooterButton} ${styles.primary}`}
                onClick={handleApply}
                loading={loading}
                icon={<FilterOutlined />}
              >
                Áp dụng
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      <DatePickerModal
        isOpen={startDatePickerOpen}
        onClose={() => setStartDatePickerOpen(false)}
        onSelect={handleStartDateSelect}
        selectedDate={getStartDateValue()}
        title="Chọn ngày bắt đầu"
      />

      <DatePickerModal
        isOpen={endDatePickerOpen}
        onClose={() => setEndDatePickerOpen(false)}
        onSelect={handleEndDateSelect}
        selectedDate={getEndDateValue()}
        title="Chọn ngày kết thúc"
      />
    </>
  );
};
